import React, { useEffect, useMemo, useRef, useState } from 'react'

import { AddPhotoAlternate, Delete, Edit } from '@mui/icons-material'
import { Box, FormControl, MenuItem, Select, TextField, Typography } from '@mui/material'
import { useFormik } from 'formik'

import { currencies } from '../../../../shared/dictionaries/currencies.dictionary'
import { FulfillmentOrderItem } from '../../CreateFulfillmentOrder.types'
import {
  ALLOWED_PHOTO_EXTENSIONS,
  ALLOWED_PHOTO_MIME_TYPES,
  INITIAL_FORM_VALUES,
  INVALID_PHOTO_TYPE_MESSAGE,
  MAX_PHOTO_SIZE_BYTES,
  MAX_PHOTO_SIZE_EXCEEDED_MESSAGE,
  PHOTO_ACCEPT_ATTRIBUTE,
  addItemValidationSchema
} from './AddItemForm.dictionary'
import styles from './AddItemForm.styles'
import { AddItemFormProps, AddItemFormValues } from './AddItemForm.types'

const AddItemForm: React.FC<AddItemFormProps> = (props) => {
  const {
    itemId,
    itemNumber,
    showRemoveButton,
    onRemove,
    onChange,
    initialValues,
    initialPhoto = null,
    initialPhotoPreview = ''
  } = props

  const [photo, setPhoto] = useState<File | null>(initialPhoto)
  const [photoPreview, setPhotoPreview] = useState<string>(initialPhotoPreview)
  const [photoError, setPhotoError] = useState<string | null>(null)

  const formik = useFormik<AddItemFormValues>({
    initialValues: initialValues || INITIAL_FORM_VALUES,
    validationSchema: addItemValidationSchema,
    onSubmit: () => undefined
  })

  const isPhotoValid = photo !== null || photoPreview !== ''

  const schemaValidationPassed = useMemo(() => {
    try {
      addItemValidationSchema.validateSync(formik.values)

      return true
    } catch {
      return false
    }
  }, [formik.values])

  const isFormValid = schemaValidationPassed && isPhotoValid

  const onChangeRef = useRef(onChange)
  onChangeRef.current = onChange

  useEffect(() => {
    if (isFormValid) {
      const validItem: FulfillmentOrderItem = {
        id: itemId,
        photo,
        photoPreview,
        description: formik.values.description,
        quantity: Number(formik.values.quantity),
        declaredValue: Number(formik.values.declaredValue),
        currency: formik.values.currency
      }

      onChangeRef.current(true, validItem)
    } else {
      onChangeRef.current(false, null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isFormValid,
    photo,
    photoPreview,
    formik.values.description,
    formik.values.quantity,
    formik.values.declaredValue,
    formik.values.currency,
    itemId
  ])

  const handlePhotoUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]

    if (file) {
      const fileExtension = file.name.toLowerCase().match(/\.[^.]+$/)?.[0] ?? ''
      const hasAllowedExtension = ALLOWED_PHOTO_EXTENSIONS.includes(fileExtension)
      const hasAllowedMimeType = ALLOWED_PHOTO_MIME_TYPES.includes(file.type)

      if (!hasAllowedExtension || !hasAllowedMimeType) {
        setPhotoError(INVALID_PHOTO_TYPE_MESSAGE)
        event.target.value = ''

        return
      }

      if (file.size > MAX_PHOTO_SIZE_BYTES) {
        setPhotoError(MAX_PHOTO_SIZE_EXCEEDED_MESSAGE)
        event.target.value = ''

        return
      }

      setPhotoError(null)

      // eslint-disable-next-line no-undef
      const reader = new FileReader()

      reader.addEventListener('load', () => {
        setPhoto(file)
        setPhotoPreview(reader.result as string)
      })

      reader.readAsDataURL(file)
    }

    event.target.value = ''
  }

  const getFieldError = (fieldName: keyof AddItemFormValues): string | undefined => {
    return formik.touched[fieldName] && formik.errors[fieldName] ? String(formik.errors[fieldName]) : undefined
  }

  const hasFieldError = (fieldName: keyof AddItemFormValues): boolean => {
    return Boolean(formik.touched[fieldName] && formik.errors[fieldName])
  }

  const photoInputId = `add-item-photo-upload-${itemId}`

  return (
    <Box sx={styles.itemCardContainer}>
      {/* Item Header */}
      <Box sx={styles.itemHeader}>
        <Box sx={styles.itemHeaderLeft}>
          <Box sx={styles.itemBadge}>
            <Typography sx={styles.itemBadgeText}>{itemNumber}</Typography>
          </Box>

          <Typography sx={styles.itemLabel}>Item {itemNumber}</Typography>
        </Box>

        {showRemoveButton && (
          <Box
            sx={styles.removeButton}
            onClick={onRemove}
            role="button"
            tabIndex={0}
            onKeyDown={(event) => event.key === 'Enter' && onRemove()}
          >
            <Delete sx={styles.removeIcon} />
            <Typography sx={styles.removeText}>Remove</Typography>
          </Box>
        )}
      </Box>

      <Box sx={styles.itemDivider} />

      {/* Photo + Fields Row */}
      <Box sx={styles.photoDescriptionRow}>
        {/* Photo Upload */}
        <Box>
          <input
            accept={PHOTO_ACCEPT_ATTRIBUTE}
            style={styles.photoUploadInput}
            id={photoInputId}
            type="file"
            onChange={handlePhotoUpload}
          />

          <label htmlFor={photoInputId}>
            {photoPreview ? (
              <Box sx={styles.photoPreviewContainer}>
                <Box component="img" src={photoPreview} alt="Item preview" sx={styles.photoPreview} />

                <Box sx={styles.editIconOverlay}>
                  <Edit sx={styles.editIcon} />
                </Box>
              </Box>
            ) : (
              <Box sx={styles.photoUploadArea}>
                <AddPhotoAlternate sx={styles.uploadIcon} />

                <Typography sx={styles.uploadLabel}>Upload Photo *</Typography>

                <Typography sx={styles.uploadHint}>Click to browse</Typography>
              </Box>
            )}
          </label>

          {photoError && (
            <Typography variant="caption" sx={styles.photoErrorText}>
              {photoError}
            </Typography>
          )}
        </Box>

        {/* Description + Quantity + Value + Currency Column */}
        <Box sx={styles.descriptionWeightColumn}>
          <Box sx={styles.descriptionQuantityRow}>
            <TextField
              name="description"
              label="Description *"
              value={formik.values.description}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={hasFieldError('description')}
              helperText={getFieldError('description')}
              fullWidth
              sx={styles.descriptionField}
            />

            <TextField
              name="quantity"
              label="Quantity *"
              value={formik.values.quantity}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={hasFieldError('quantity')}
              helperText={getFieldError('quantity')}
              sx={styles.quantityField}
              type="number"
              inputProps={{ min: 1, max: 100 }}
            />
          </Box>

          <Box sx={styles.valueRow}>
            <TextField
              name="declaredValue"
              label="Declared Value *"
              value={formik.values.declaredValue}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={hasFieldError('declaredValue')}
              helperText={getFieldError('declaredValue')}
              sx={styles.declaredValueField}
              type="number"
            />

            <FormControl sx={styles.currencyField}>
              <Select
                name="currency"
                value={formik.values.currency}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                displayEmpty
              >
                {currencies.map((currencyOption) => (
                  <MenuItem key={currencyOption.value} value={currencyOption.value}>
                    {currencyOption.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Box>
      </Box>
    </Box>
  )
}

export default AddItemForm
